<script setup>
const props = defineProps({
  isDisabled: {
    type: Boolean,
    default: false,
  },

  placeholder: {
    type: String,
    default: '',
  },

  showErrors: {
    type: Boolean,
    default: true,
  },

  isRequired: {
    type: Boolean,
    default: false,
  },

  name: {
    type: String,
    default: '',
  },

  validateConfig: {
    type: Object,
    default: () => ({
      validateOnBlur: false,
      validateOnChange: true,
      validateOnInput: false,
      validateOnModelUpdate: true,
    }),
  },
});

const emit = defineEmits(['focus', 'blur']);
const modelValue = defineModel({
  type: [String, Number],
  default: '',
});

//<editor-fold desc="FOCUS_BLUR">
const focused = ref(false);

function setFocus(value) {
  focused.value = Boolean(value);
}

function onInputFocus(event) {
  setFocus(true);
  emit('focus', event);
}

function onInputBlur(event) {
  setFocus(false);
  emit('blur', event);
}
//</editor-fold>

//<editor-fold desc="Availability">
const currentInstance = ref(null);

onMounted(() => {
  currentInstance.value = getCurrentInstance();
});

const describedById = computed(() => {
  return `error-${currentInstance.uid}`;
});
//</editor-fold>
</script>

<template>
  <VeeField
    v-slot="{ field, errorMessage }"
    :model-value="modelValue"
    :name="props.name"
    :type="props.type"
    v-bind="{
      ...props.validateConfig,
    }"
    as="div"
  >
    <div
      class="ui-form-textarea"
      :class="[
        {
          'ui-form-textarea--inner-indent': props.showErrors,
          'ui-form-textarea--disabled': props.isDisabled,
          'ui-form-textarea--error': Boolean(errorMessage),
          'ui-form-textarea--active': modelValue || focused,
        },
      ]"
    >
      <div class="ui-form-textarea__body">
        <textarea
          ref="input"
          v-bind="field"
          v-model.trim="modelValue"
          class="ui-form-textarea__field"
          autocomplete="off"
          :disabled="props.isDisabled"
          :required="props.isRequired"
          :placeholder="props.placeholder"
          :aria-describedby="describedById"
          :aria-required="props.isRequired"
          :aria-invalid="Boolean(props.showErrors && errorMessage)"
          @focus="onInputFocus"
          @blur="onInputBlur"
        />
      </div>

      <div
        v-show="props.showErrors && errorMessage"
        class="ui-form-textarea__footer"
        :title="errorMessage"
      >
        <span
          :id="describedById"
          aria-live="assertive"
          class="ui-form-textarea__font ui-form-textarea__font--error"
          v-html="errorMessage"
        />
      </div>
    </div>
  </VeeField>
</template>

<style scoped lang="scss">
.ui-form-textarea {
  $parent: &;

  position: relative;

  &__font {
    &--error {
      font-size: em(10);
      font-weight: 600;
      line-height: 120%;

      @include media-breakpoint-down(md) {
        font-size: em(8);
      }
    }
  }

  &__body {
    position: relative;
    display: block;
  }

  &__footer {
    position: absolute;
    bottom: em(4);
    left: 0;
    display: flex;
    align-items: center;
    width: 100%;
    color: $accent-red;
    text-align: left;

    @include media-breakpoint-down(md) {
      bottom: em(3);
    }
  }

  &__field {
    $font-size: 12;
    $font-size-mobile: 9;

    width: 100%;
    height: 100%;
    min-height: em(254, $font-size);
    padding: em(10, $font-size);
    font-size: em($font-size);
    font-weight: 800;
    color: $text-dark-secondary;
    text-transform: uppercase;
    resize: none;
    background-color: $color-white;
    border: none;
    border-radius: em(5);

    &::placeholder {
      opacity: 0.7;
    }

    @include media-breakpoint-down(md) {
      min-height: em(203, $font-size-mobile);
      padding: em(8, $font-size-mobile);
      font-size: em($font-size-mobile);
    }

    @include media-breakpoint-down(sm) {
      min-height: em(137, $font-size);
      padding: em(10, $font-size) em(11, $font-size);
      font-size: em($font-size);
    }
  }

  &--inner-indent {
    padding-bottom: em(20);

    @include media-breakpoint-down(md) {
      padding-bottom: em(16);
    }

    @include media-breakpoint-down(sm) {
      padding-bottom: em(14);
    }
  }

  &--error {
    #{$parent} {
      &__field {
        color: $accent-red;
      }
    }
  }

  &--disabled {
    #{$parent} {
    }
  }
}
</style>
